import { useSaveBillablePrice } from '@domains/subscription/priv/api/queries'
import { useCreateBillableTemplatePrice } from '@domains/subscription/priv/Billables/api/query'
import type { BillableFormPluginProps, PriceRowPlugin } from '@domains/subscription/priv/Billables/usecases/BillableForm/plugin'
import type { CreatePricePlugin, CreatePricePluginProps } from '@domains/subscription/priv/Billables/usecases/CreatePriceForm'
import { MaxPriceForm } from '@domains/subscription/priv/Billables/usecases/Price/max/MaxPriceForm'
import { MaxPriceRow } from '@domains/subscription/priv/Billables/usecases/Price/max/MaxPriceRow'
import { isMax } from '@domains/subscription/priv/Billables/usecases/Price/max/maxMapper'
import type { BaseAlgorithm, BasePrice } from '@domains/subscription/priv/components/Confirmable/confirmable'
import type { CreatePriceRequest } from '@domains/subscription/priv/types'
import { type ComponentType, memo } from 'react'

const MaxPriceFormFactory = (props: CreatePricePluginProps) => {
  const { mutate } = useCreateBillableTemplatePrice(props.billableId, props.templateId)

  const handleCreatePrice = (price: BasePrice) => {
    if (!isMax(price)) {
      throw new Error(`Attempted to create a non-max price with MaxPriceFormFactory: ${price.type}`)
    }

    mutate(price, {
      onSuccess: () => {
        props.onCreatedPrice(price)
      },
    })
  }

  const getAlgorithmPlugin = (algorithm: BaseAlgorithm) => {
    const plugin = props.additionalPlugins?.find((plugin) => plugin.supports(algorithm.type))

    return plugin?.FormComponent
  }

  return <MaxPriceForm {...props} getAlgorithmPlugin={getAlgorithmPlugin} onCreatedPrice={handleCreatePrice} />
}

export class MaxCreatePricePlugin implements CreatePricePlugin {
  constructor(public additionalPlugins?: CreatePricePlugin[]) {}
  type = 'Max'
  supports(type): boolean {
    return type === 'Max'
  }
  Component: ComponentType<CreatePricePluginProps> = MaxPriceFormFactory
}

const MaxPriceRowFactory = memo((props: BillableFormPluginProps) => {
  const { mutateAsync: saveBillable } = useSaveBillablePrice(props.templateId)

  const handleSave = async (data: CreatePriceRequest) => {
    await saveBillable({
      billableId: data.billableId,
      billablePrice: data,
    })
  }

  if (!isMax(props.price)) {
    return null
  }

  return <MaxPriceRow {...props} additionalPlugins={props.additionalPlugins} maxPrice={props.price} onSubmit={handleSave} />
})

export class MaxPriceRowPlugin implements PriceRowPlugin {
  constructor(public additionalPlugins: PriceRowPlugin[]) {}
  type = 'Max'

  supports(algorithm): boolean {
    return algorithm.type === this.type
  }

  Component: ComponentType<BillableFormPluginProps> = MaxPriceRowFactory
}
